import { useTranslation } from '@kostbar/i18n'
import type { CountVarianceReason } from '@/api/endpoints/inventory-count-sessions'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'

export const COUNT_REASONS: Array<CountVarianceReason> = [
  'BREAKAGE',
  'EXPIRY',
  'THEFT',
  'MISCOUNT',
  'UNKNOWN',
]

interface CountReasonPickerProps {
  value: CountVarianceReason | undefined
  onChange: (reason: CountVarianceReason | undefined) => void
}

/**
 * The five variance reasons, at the platform's minimum touch size — `ToggleGroupItem`'s own 36 px
 * is below it, which is why every item carries `min-h-11`.
 *
 * The selected state is overridden to the primary colour: the shared toggle's `data-[state=on]`
 * paints `bg-accent`, which is zinc-100 — all but invisible next to an unselected outline pill on
 * a white sheet, and this choice is a mandatory one the counter has to see they have made. The
 * override is scoped here rather than in `toggle.tsx`, which every other toggle group shares.
 *
 * Used twice: once per variance group, and once inside a row that overrides its group's reason.
 * Batching is only about the *asking*: each confirm still posts its own reason on its own per-item
 * resolve, so the reason stays a property of the article.
 */
export function CountReasonPicker({ value, onChange }: CountReasonPickerProps) {
  const { t } = useTranslation('inventory-count')
  return (
    <ToggleGroup
      type="single"
      variant="outline"
      size="lg"
      spacing={2}
      value={value ?? ''}
      onValueChange={(next) =>
        onChange(next === '' ? undefined : (next as CountVarianceReason))
      }
      className="flex-wrap justify-start"
    >
      {COUNT_REASONS.map((reason) => (
        <ToggleGroupItem
          key={reason}
          value={reason}
          className="min-h-11 data-[state=on]:border-primary data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
        >
          {t(`review.reason.${reason}`)}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  )
}
